"use client";

import type { TimelineItem } from "../hooks/timeline-types";
import { TimelineItemComponent } from "./TimelineItem";
import { getChannelVisual } from "../utils/channel-visuals";

interface TimelineItemRowProps {
  item: TimelineItem;
  compact: boolean;
  threadEntriesOnPage: number | undefined;
  /** True for the first few rows — eager-load their S3 email body on mount. */
  eagerBody?: boolean;
  /** True when this is the last row in its day group (stop the spine line). */
  isLast?: boolean;
  onItemClick: (item: TimelineItem) => void;
  onCopyLink: (item: TimelineItem) => void;
}

/**
 * One row of the single-column timeline feed: a left rail (continuous spine +
 * a channel-colored dot carrying the ONLY channel icon on the row) beside the
 * item card. Replaces the old inbound-left / outbound-right chat-bubble layout —
 * direction is now a small tag inside the card, not full-width alignment.
 */
export function TimelineItemRow({
  item,
  compact,
  threadEntriesOnPage,
  eagerBody,
  isLast,
  onItemClick,
  onCopyLink,
}: TimelineItemRowProps) {
  const { Icon, chipClass, iconClass, label } = getChannelVisual(item);
  const dotSize = compact ? "h-6 w-6" : "h-8 w-8";
  const iconSize = compact ? "h-3 w-3" : "h-4 w-4";

  return (
    <div className="flex gap-3">
      <div className="relative flex flex-shrink-0 justify-center" style={{ width: compact ? 24 : 32 }}>
        {/* Continuous spine — hidden on the last row of a group. */}
        {!isLast && (
          <div
            className="absolute top-0 bottom-0 w-px bg-border"
            aria-hidden="true"
          />
        )}
        <div
          className={`relative z-10 flex ${dotSize} items-center justify-center rounded-full ring-4 ring-background ${chipClass}`}
          title={label}
        >
          <Icon className={`${iconSize} ${iconClass}`} aria-label={label} />
        </div>
      </div>

      <div className={`min-w-0 flex-1 ${compact ? "pb-2" : "pb-4"}`}>
        <TimelineItemComponent
          item={item}
          compact={compact}
          onClick={onItemClick}
          onCopyLink={onCopyLink}
          threadEntriesOnPage={threadEntriesOnPage}
          eagerBody={eagerBody}
        />
      </div>
    </div>
  );
}
